import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import callApi from '../../util/apiCaller';

// Action types
export const PASSKEY_REGISTRATION_OPTIONS_REQUEST = 'PASSKEY_REGISTRATION_OPTIONS_REQUEST';
export const PASSKEY_REGISTRATION_OPTIONS_SUCCESS = 'PASSKEY_REGISTRATION_OPTIONS_SUCCESS';
export const PASSKEY_REGISTRATION_OPTIONS_ERROR = 'PASSKEY_REGISTRATION_OPTIONS_ERROR';

export const PASSKEY_REGISTER_REQUEST = 'PASSKEY_REGISTER_REQUEST';
export const PASSKEY_REGISTER_SUCCESS = 'PASSKEY_REGISTER_SUCCESS';
export const PASSKEY_REGISTER_ERROR = 'PASSKEY_REGISTER_ERROR';

export const PASSKEY_AUTHENTICATION_OPTIONS_REQUEST = 'PASSKEY_AUTHENTICATION_OPTIONS_REQUEST';
export const PASSKEY_AUTHENTICATION_OPTIONS_SUCCESS = 'PASSKEY_AUTHENTICATION_OPTIONS_SUCCESS';
export const PASSKEY_AUTHENTICATION_OPTIONS_ERROR = 'PASSKEY_AUTHENTICATION_OPTIONS_ERROR';

export const PASSKEY_AUTHENTICATE_REQUEST = 'PASSKEY_AUTHENTICATE_REQUEST';
export const PASSKEY_AUTHENTICATE_SUCCESS = 'PASSKEY_AUTHENTICATE_SUCCESS';
export const PASSKEY_AUTHENTICATE_ERROR = 'PASSKEY_AUTHENTICATE_ERROR';

export const PASSKEY_LIST_REQUEST = 'PASSKEY_LIST_REQUEST';
export const PASSKEY_LIST_SUCCESS = 'PASSKEY_LIST_SUCCESS';
export const PASSKEY_LIST_ERROR = 'PASSKEY_LIST_ERROR';

export const PASSKEY_DELETE_REQUEST = 'PASSKEY_DELETE_REQUEST';
export const PASSKEY_DELETE_SUCCESS = 'PASSKEY_DELETE_SUCCESS';
export const PASSKEY_DELETE_ERROR = 'PASSKEY_DELETE_ERROR';

export const PASSKEY_RENAME_REQUEST = 'PASSKEY_RENAME_REQUEST';
export const PASSKEY_RENAME_SUCCESS = 'PASSKEY_RENAME_SUCCESS';
export const PASSKEY_RENAME_ERROR = 'PASSKEY_RENAME_ERROR';

export const PASSKEY_CHECK_REQUEST = 'PASSKEY_CHECK_REQUEST';
export const PASSKEY_CHECK_SUCCESS = 'PASSKEY_CHECK_SUCCESS';
export const PASSKEY_CHECK_ERROR = 'PASSKEY_CHECK_ERROR';

export const CLEAR_PASSKEY_ERROR = 'CLEAR_PASSKEY_ERROR';

// Action creators
export const passkeyRegistrationOptionsRequest = () => ({
  type: PASSKEY_REGISTRATION_OPTIONS_REQUEST,
});

export const passkeyRegistrationOptionsSuccess = options => ({
  type: PASSKEY_REGISTRATION_OPTIONS_SUCCESS,
  payload: options,
});

export const passkeyRegistrationOptionsError = error => ({
  type: PASSKEY_REGISTRATION_OPTIONS_ERROR,
  payload: error,
});

export const passkeyRegisterRequest = () => ({
  type: PASSKEY_REGISTER_REQUEST,
});

export const passkeyRegisterSuccess = data => ({
  type: PASSKEY_REGISTER_SUCCESS,
  payload: data,
});

export const passkeyRegisterError = error => ({
  type: PASSKEY_REGISTER_ERROR,
  payload: error,
});

export const passkeyAuthenticationOptionsRequest = () => ({
  type: PASSKEY_AUTHENTICATION_OPTIONS_REQUEST,
});

export const passkeyAuthenticationOptionsSuccess = options => ({
  type: PASSKEY_AUTHENTICATION_OPTIONS_SUCCESS,
  payload: options,
});

export const passkeyAuthenticationOptionsError = error => ({
  type: PASSKEY_AUTHENTICATION_OPTIONS_ERROR,
  payload: error,
});

export const passkeyAuthenticateRequest = () => ({
  type: PASSKEY_AUTHENTICATE_REQUEST,
});

export const passkeyAuthenticateSuccess = data => ({
  type: PASSKEY_AUTHENTICATE_SUCCESS,
  payload: data,
});

export const passkeyAuthenticateError = error => ({
  type: PASSKEY_AUTHENTICATE_ERROR,
  payload: error,
});

export const passkeyListRequest = () => ({
  type: PASSKEY_LIST_REQUEST,
});

export const passkeyListSuccess = passkeys => ({
  type: PASSKEY_LIST_SUCCESS,
  payload: passkeys,
});

export const passkeyListError = error => ({
  type: PASSKEY_LIST_ERROR,
  payload: error,
});

export const passkeyDeleteRequest = () => ({
  type: PASSKEY_DELETE_REQUEST,
});

export const passkeyDeleteSuccess = (passkeyId, hasPasskeys) => ({
  type: PASSKEY_DELETE_SUCCESS,
  payload: { passkeyId, hasPasskeys },
});

export const passkeyDeleteError = error => ({
  type: PASSKEY_DELETE_ERROR,
  payload: error,
});

export const passkeyRenameRequest = () => ({
  type: PASSKEY_RENAME_REQUEST,
});

export const passkeyRenameSuccess = (passkeyId, name) => ({
  type: PASSKEY_RENAME_SUCCESS,
  payload: { passkeyId, name },
});

export const passkeyRenameError = error => ({
  type: PASSKEY_RENAME_ERROR,
  payload: error,
});

export const passkeyCheckRequest = () => ({
  type: PASSKEY_CHECK_REQUEST,
});

export const passkeyCheckSuccess = (hasPasskeys, count) => ({
  type: PASSKEY_CHECK_SUCCESS,
  payload: { hasPasskeys, count },
});

export const passkeyCheckError = error => ({
  type: PASSKEY_CHECK_ERROR,
  payload: error,
});

export const clearPasskeyError = () => ({
  type: CLEAR_PASSKEY_ERROR,
});

// Thunk actions
export function registerPasskey(name = 'Passkey') {
  return async (dispatch, _getState) => {
    try {
      dispatch(passkeyRegistrationOptionsRequest());

      // Get registration options from server
      const optionsResponse = await callApi(
        dispatch,
        'auth/passkey/registration-options',
        null,
        'post',
      );
      dispatch(passkeyRegistrationOptionsSuccess(optionsResponse));

      // Start WebAuthn registration
      const credential = await startRegistration(optionsResponse);

      dispatch(passkeyRegisterRequest());

      // Send credential to server
      const registerResponse = await callApi(dispatch, 'auth/passkey/register', null, 'post', {
        credential,
        name,
        challengeId: optionsResponse.challengeId,
      });

      dispatch(passkeyRegisterSuccess(registerResponse));

      // Refresh passkey list
      dispatch(listPasskeys());

      return registerResponse;
    } catch (error) {
      const errorMessage = error.message || 'Passkey registration failed';
      dispatch(passkeyRegisterError(errorMessage));
      throw error;
    }
  };
}

export function authenticatePasskey(email = null, userId = null) {
  return async (dispatch, _getState) => {
    try {
      dispatch(passkeyAuthenticationOptionsRequest());

      // Prepare request body
      const requestBody = {};
      if (email) requestBody.email = email;
      if (userId) requestBody.userId = userId;

      // Get authentication options from server
      const optionsResponse = await callApi(
        dispatch,
        'public/auth/passkey/authentication-options',
        null,
        'post',
        requestBody,
      );
      dispatch(passkeyAuthenticationOptionsSuccess(optionsResponse));

      // Start WebAuthn authentication
      const credential = await startAuthentication(optionsResponse);

      dispatch(passkeyAuthenticateRequest());

      // Send credential to server
      const authResponse = await callApi(
        dispatch,
        'public/auth/passkey/authenticate',
        null,
        'post',
        {
          credential,
          challengeId: optionsResponse.challengeId,
        },
      );

      dispatch(passkeyAuthenticateSuccess(authResponse));
      return authResponse;
    } catch (error) {
      const errorMessage = error.message || 'Passkey authentication failed';
      dispatch(passkeyAuthenticateError(errorMessage));
      throw error;
    }
  };
}

export function listPasskeys() {
  return async (dispatch, _getState) => {
    try {
      dispatch(passkeyListRequest());

      const response = await callApi(dispatch, 'auth/passkey/list', null, 'get');
      dispatch(passkeyListSuccess(response.passkeys));
      return response;
    } catch (error) {
      const errorMessage = error.message || 'Failed to load passkeys';
      dispatch(passkeyListError(errorMessage));
      throw error;
    }
  };
}

export function deletePasskey(passkeyId) {
  return async (dispatch, _getState) => {
    try {
      dispatch(passkeyDeleteRequest());

      const response = await callApi(dispatch, `auth/passkey/${passkeyId}`, null, 'delete');
      dispatch(passkeyDeleteSuccess(passkeyId, response.hasPasskeys));
      return true;
    } catch (error) {
      const errorMessage = error.message || 'Failed to delete passkey';
      dispatch(passkeyDeleteError(errorMessage));
      throw error;
    }
  };
}

export function renamePasskey(passkeyId, name) {
  return async (dispatch, _getState) => {
    try {
      dispatch(passkeyRenameRequest());

      await callApi(dispatch, `auth/passkey/${passkeyId}`, null, 'put', { name });
      dispatch(passkeyRenameSuccess(passkeyId, name));
      return true;
    } catch (error) {
      const errorMessage = error.message || 'Failed to rename passkey';
      dispatch(passkeyRenameError(errorMessage));
      throw error;
    }
  };
}

export function checkPasskeys(email) {
  return async dispatch => {
    try {
      dispatch(passkeyCheckRequest());

      // Use null as token since this is a public endpoint
      const response = await callApi(dispatch, 'public/auth/passkey/check', null, 'post', {
        email,
      });
      dispatch(passkeyCheckSuccess(response.hasPasskeys, response.count));
      return response;
    } catch (error) {
      const errorMessage = error.message || 'Failed to check passkeys';
      dispatch(passkeyCheckError(errorMessage));
      throw error;
    }
  };
}
